import TwoFactorVerify from './TwoFactorVerify';
/**
 * Vacation Rental Manager Login
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * Dedicated login page for vacation rental property managers
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Palmtree, LogIn, Home, ArrowLeft } from 'lucide-react';

const VacationRentalLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [twoFAUserId, setTwoFAUserId] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { 
        email: loginForm.email, 
        password: loginForm.password 
      });
      
      if (data.requires_2fa) {
        setShow2FA(true);
        setTwoFAEmail(data.email);
        setTwoFAUserId(data.user_id);
        setLoading(false);
        return;
      }
      if (data.access_token && data.role) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        if (data.org_type) localStorage.setItem("org_type", data.org_type);
        if (data.user_id) localStorage.setItem("user_id", data.user_id);
        
        toast.success("Welcome back! Redirecting to your dashboard...");
        
        // Redirect to vacation rentals dashboard
        setTimeout(() => { 
          window.location.replace("/vacation-rentals"); 
        }, 100);
      } else {
        toast.error("Invalid response from server");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Login failed: " + (err.response?.data?.detail || "Invalid email or password"));
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-12 px-4">
        <TwoFactorVerify
          email={twoFAEmail}
          userId={twoFAUserId}
          onSuccess={(data) => {
            toast.success("Login successful!");
            setTimeout(() => { window.location.replace("/dashboard"); }, 100);
          }}
          onBack={() => { setShow2FA(false); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <Palmtree className="h-8 w-8 text-emerald-600" />
            <span className="text-xl font-bold text-gray-900">Estate Flow</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/vacation-rentals/search')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Button>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Palmtree className="h-10 w-10 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Vacation Rental Manager</CardTitle>
            <CardDescription>
              Sign in to manage your vacation rental properties
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="vr-login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  type="email" 
                  placeholder="your@email.com" 
                  value={loginForm.email} 
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} 
                  required 
                  data-testid="vr-login-email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">Password</Label>
                  <button 
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input 
                  id="password"
                  type="password" 
                  value={loginForm.password} 
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} 
                  required 
                  data-testid="vr-login-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading}
                data-testid="vr-login-submit"
              >
                {loading ? 'Signing in...' : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign In
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 space-y-3">
              <div className="text-center">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <button 
                    onClick={() => navigate('/signup?role=vacation_rental')}
                    className="text-emerald-600 hover:underline font-medium"
                  >
                    Sign up as Property Manager
                  </button>
                </p>
              </div>
              
              <div className="border-t pt-3">
                <p className="text-sm text-gray-500 text-center">
                  Looking to book a vacation rental?{' '}
                  <button 
                    onClick={() => navigate('/guest/login')}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Guest Sign In
                  </button>
                </p>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => navigate('/')}>
                <Home className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VacationRentalLogin;
